import { useEffect, useRef, useState } from "react";
import { Mic, Upload, Square, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { useLanguage } from "@/contexts/LanguageContext";

/** Hard upper bound enforced both client- and server-side. Matches
 *  `BYO_AUDIO_MAX_DURATION_SEC` in generateTalkingAvatarService.ts. */
export const USER_AUDIO_MAX_SECONDS = 30;

/** Lower bound: Kling Avatar rejects audio under 2 s, and a clip that short
 *  would be mostly silence-padding rather than real speech to lip-sync to.
 *  Matches `BYO_AUDIO_MIN_DURATION_SEC` on the server. */
export const USER_AUDIO_MIN_SECONDS = 2;

/** Kling Avatar accepts only mp3/wav/m4a/aac/mpeg — WebM (Chrome/Firefox
 *  MediaRecorder default) is rejected. Prefer mp4/AAC for recording so the
 *  output is Kling-compatible without server-side transcoding. */
const RECORDER_MIME_CANDIDATES = [
  "audio/mp4",
  "audio/aac",
  "audio/mpeg",
  "audio/wav",
] as const;
function pickRecorderMimeType(): string | undefined {
  if (typeof MediaRecorder === "undefined") return undefined;
  for (const type of RECORDER_MIME_CANDIDATES) {
    if (MediaRecorder.isTypeSupported(type)) return type;
  }
  return undefined;
}

/** Browser-friendly extensions that Kling accepts. Used both when naming the
 *  blob produced by MediaRecorder and to validate uploaded files. */
const ALLOWED_UPLOAD_EXTENSIONS = ["mp3", "wav", "m4a", "mp4", "aac"] as const;

interface TalkingAvatarUserAudioModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called with the recorded/uploaded audio + its duration (seconds).
   *  Parent handles upload to R2 + the render API call. */
  onSubmit: (params: {
    audio: Blob;
    filename: string;
    durationSec: number;
  }) => Promise<void>;
}

/** Compute the duration of an audio Blob using a hidden <audio> element. */
async function probeDuration(blob: Blob): Promise<number> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(blob);
    const audio = document.createElement("audio");
    audio.preload = "metadata";
    audio.onloadedmetadata = () => {
      const dur = audio.duration;
      URL.revokeObjectURL(url);
      if (!Number.isFinite(dur) || dur <= 0) {
        reject(new Error("Could not read audio duration"));
        return;
      }
      resolve(dur);
    };
    audio.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Could not read audio file"));
    };
    audio.src = url;
  });
}

export function TalkingAvatarUserAudioModal({
  open,
  onOpenChange,
  onSubmit,
}: TalkingAvatarUserAudioModalProps) {
  const { t } = useLanguage();

  const [audio, setAudio] = useState<{
    blob: Blob;
    filename: string;
    durationSec: number;
    previewUrl: string;
  } | null>(null);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingSeconds, setRecordingSeconds] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // MediaRecorder lifecycle — refs so re-renders don't kill the recorder.
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const tickerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Reset everything when the modal closes (or revoke object URL when audio changes).
  useEffect(() => {
    if (!open) {
      stopRecording(false);
      if (audio?.previewUrl) URL.revokeObjectURL(audio.previewUrl);
      setAudio(null);
      setRecordingSeconds(0);
      setIsSubmitting(false);
      setError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Auto-stop the recording when it hits the cap so the user can't blow past it.
  useEffect(() => {
    if (isRecording && recordingSeconds >= USER_AUDIO_MAX_SECONDS) {
      stopRecording(true);
    }
  }, [isRecording, recordingSeconds]);

  function stopRecording(commit: boolean) {
    if (tickerRef.current) {
      clearInterval(tickerRef.current);
      tickerRef.current = null;
    }
    const rec = recorderRef.current;
    if (rec && rec.state !== "inactive") {
      if (commit) {
        rec.stop();
      } else {
        rec.stream.getTracks().forEach((tr) => tr.stop());
        rec.stop();
        chunksRef.current = [];
      }
    }
    recorderRef.current = null;
    setIsRecording(false);
  }

  async function startRecording() {
    setError(null);
    const mimeType = pickRecorderMimeType();
    if (!mimeType) {
      // Browser only supports WebM (e.g. older Firefox). Kling will reject
      // that, so we steer the user to the upload path instead.
      setError(t.talkingAvatar.userAudioErrorUnsupportedRecord);
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream, { mimeType });
      chunksRef.current = [];
      rec.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };
      rec.onstop = async () => {
        const blob = new Blob(chunksRef.current, { type: rec.mimeType || mimeType });
        // Pick a Kling-friendly extension based on the actual mime — mp4 →
        // m4a (same container, Kling-accepted name), everything else maps
        // straight from the second mime token.
        const subtype = (rec.mimeType || mimeType).split("/")[1].split(";")[0];
        const ext = subtype === "mp4" ? "m4a" : subtype === "mpeg" ? "mp3" : subtype;
        stream.getTracks().forEach((tr) => tr.stop());
        try {
          const durationSec = await probeDuration(blob);
          // Keep the clip visible (so the user sees what they recorded) but
          // flag it as too short — canSubmit + the inline error block submit.
          setAudio({
            blob,
            filename: `recording.${ext}`,
            durationSec,
            previewUrl: URL.createObjectURL(blob),
          });
          if (durationSec < USER_AUDIO_MIN_SECONDS) {
            setError(
              t.talkingAvatar.userAudioErrorTooShort.replace(
                "{min}",
                String(USER_AUDIO_MIN_SECONDS),
              ),
            );
          }
        } catch (err) {
          setError(err instanceof Error ? err.message : t.talkingAvatar.userAudioErrorRead);
        }
      };
      rec.start();
      recorderRef.current = rec;
      setIsRecording(true);
      setRecordingSeconds(0);
      tickerRef.current = setInterval(() => {
        setRecordingSeconds((s) => s + 1);
      }, 1000);
    } catch {
      setError(t.talkingAvatar.userAudioErrorMic);
    }
  }

  async function handleFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setError(null);
    // Kling Avatar rejects WebM/Ogg containers — block at upload time so the
    // user gets a clear error instead of a generic "failed" after ~30s.
    const ext = (file.name.split(".").pop() ?? "").toLowerCase();
    if (!(ALLOWED_UPLOAD_EXTENSIONS as readonly string[]).includes(ext)) {
      setError(t.talkingAvatar.userAudioErrorUnsupportedFormat);
      return;
    }
    try {
      const durationSec = await probeDuration(file);
      if (durationSec > USER_AUDIO_MAX_SECONDS) {
        setError(
          t.talkingAvatar.userAudioErrorTooLong.replace(
            "{max}",
            String(USER_AUDIO_MAX_SECONDS),
          ),
        );
        return;
      }
      if (durationSec < USER_AUDIO_MIN_SECONDS) {
        setError(
          t.talkingAvatar.userAudioErrorTooShort.replace(
            "{min}",
            String(USER_AUDIO_MIN_SECONDS),
          ),
        );
        return;
      }
      setAudio({
        blob: file,
        filename: file.name,
        durationSec,
        previewUrl: URL.createObjectURL(file),
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : t.talkingAvatar.userAudioErrorRead);
    }
  }

  function clearAudio() {
    if (audio?.previewUrl) URL.revokeObjectURL(audio.previewUrl);
    setAudio(null);
    setError(null);
  }

  async function submit() {
    if (!audio || isSubmitting) return;
    setIsSubmitting(true);
    try {
      await onSubmit({
        audio: audio.blob,
        filename: audio.filename,
        durationSec: audio.durationSec,
      });
      onOpenChange(false);
    } finally {
      setIsSubmitting(false);
    }
  }

  const canSubmit =
    !!audio &&
    audio.durationSec >= USER_AUDIO_MIN_SECONDS &&
    !isRecording &&
    !isSubmitting &&
    !error;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t.talkingAvatar.userAudioModalTitle}</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground">
          {t.talkingAvatar.userAudioModalDescription.replace(
            "{max}",
            String(USER_AUDIO_MAX_SECONDS),
          )}
        </p>

        <div className="space-y-4 mt-2">
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={isRecording ? () => stopRecording(true) : startRecording}
              disabled={isSubmitting}
              className={cn(
                "inline-flex items-center justify-center gap-2 px-3 py-2 rounded-xl border text-sm font-medium transition-colors",
                isRecording
                  ? "border-destructive bg-destructive/10 text-destructive"
                  : "border-border bg-card hover:bg-secondary text-foreground",
              )}
            >
              {isRecording ? (
                <>
                  <Square className="w-4 h-4" />
                  {t.talkingAvatar.userAudioStopCta}
                </>
              ) : (
                <>
                  <Mic className="w-4 h-4" />
                  {t.talkingAvatar.userAudioRecordCta}
                </>
              )}
            </button>
            <label
              className={cn(
                "inline-flex items-center justify-center gap-2 px-3 py-2 rounded-xl border border-border bg-card hover:bg-secondary text-foreground text-sm font-medium cursor-pointer",
                (isRecording || isSubmitting) && "opacity-50 pointer-events-none",
              )}
            >
              <Upload className="w-4 h-4" />
              {t.talkingAvatar.userAudioUploadCta}
              <input
                ref={fileInputRef}
                type="file"
                accept="audio/*"
                className="hidden"
                onChange={handleFile}
                disabled={isRecording || isSubmitting}
              />
            </label>
          </div>

          {isRecording && (
            <p
              className={cn(
                "text-xs",
                recordingSeconds >= USER_AUDIO_MAX_SECONDS - 5
                  ? "text-amber-600 dark:text-amber-400"
                  : "text-muted-foreground",
              )}
            >
              {t.talkingAvatar.userAudioRecordingHint
                .replace("{seconds}", String(recordingSeconds))
                .replace("{max}", String(USER_AUDIO_MAX_SECONDS))}
            </p>
          )}

          {audio && (
            <div className="rounded-xl border border-border/60 bg-card/50 p-3 space-y-2">
              <div className="flex items-center justify-between text-xs">
                <span className="text-muted-foreground truncate max-w-[60%]">
                  {audio.filename}
                </span>
                <span className="text-muted-foreground">
                  {t.talkingAvatar.userAudioDurationLabel.replace(
                    "{seconds}",
                    audio.durationSec.toFixed(1),
                  )}
                </span>
              </div>
              <audio src={audio.previewUrl} controls className="w-full" />
              <button
                type="button"
                onClick={clearAudio}
                className="text-xs text-destructive hover:underline"
              >
                {t.talkingAvatar.userAudioReplaceCta}
              </button>
            </div>
          )}

          {error && <p className="text-xs text-destructive">{error}</p>}

          <div className="flex justify-end gap-2 pt-2 border-t border-border/50">
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
              className="px-4 py-2 rounded-xl border border-border bg-card hover:bg-secondary text-sm font-medium text-foreground disabled:opacity-50"
            >
              {t.talkingAvatar.userAudioCancel}
            </button>
            <button
              type="button"
              onClick={submit}
              disabled={!canSubmit}
              className={cn(
                "inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-gradient-to-r gradient-primary text-white text-sm font-bold transition-all",
                "shadow-[0_4px_12px_rgba(0,0,0,0.15)] disabled:opacity-50 disabled:cursor-not-allowed",
              )}
            >
              {isSubmitting && <Loader2 className="w-4 h-4 animate-spin" />}
              {isSubmitting
                ? t.talkingAvatar.userAudioSubmitting
                : t.talkingAvatar.userAudioSubmit}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
